/**
 * @fileoverview Room Calendar View Container
 * @module features/coordinator/planner/RoomCalendarView
 * @description Main calendar container with view selector (Day/Week/Month) and date navigation
 * @created 10-10-2025 20:58:00
 * @author MCHD Room Scheduler Team
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Paper,
  Text,
  Group,
  Stack,
  SegmentedControl,
  ActionIcon,
  Button,
  Badge,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCalendarEvent,
  IconLayoutGrid,
  IconLayoutList,
  IconCalendar,
} from '@tabler/icons-react';
import { DayView } from './DayView';
import { WeekView } from './WeekView';
import { MonthView } from './MonthView';
import {
  navigateNext,
  navigatePrevious,
  getTodayMidnight,
  formatCalendarHeader,
  getDateRangeForView,
} from './utils/calendarHelpers';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export type CalendarViewMode = 'day' | 'week' | 'month';

export interface SessionTemplate {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

export interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  floorId: string;
  floor: {
    id: string;
    name: string;
    level: number;
    buildingId: string;
    building: {
      id: string;
      name: string;
      code: string;
    };
  };
}

export interface Assignment {
  id: string;
  title: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  published: boolean;
  conflictCode?: string;
  users?: {
    id: string;
    name: string;
  };
}

export interface CellStatus {
  roomId: string;
  sessionTemplateId: string;
  date: string;
  status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  assignment?: Assignment;
  conflicts?: string[];
  holiday?: any;
}

export interface RoomCalendarViewProps {
  /** Rooms data */
  rooms: Room[];
  
  /** Session templates/times */
  sessionTemplates: SessionTemplate[];
  
  /** Cell statuses from API */
  cellStatuses: CellStatus[];
  
  /** Full assignments array with nested data */
  assignments?: Assignment[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Initial view mode */
  initialViewMode?: CalendarViewMode;
  
  /** Initial date */
  initialDate?: Date;
  
  /** Callback when cell is clicked for assignment creation */
  onCellClick?: ((roomId: string, sessionId: string, date: Date) => void) | undefined;
  
  /** Callback when assignment is clicked */
  onAssignmentClick?: ((assignment: Assignment) => void) | undefined;
  
  /** Callback when date range changes (for API refetch) */
  onDateRangeChange?: ((startDate: string, endDate: string) => void) | undefined;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const VIEW_MODE_STORAGE_KEY = 'room-planner-view-mode';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Room Calendar View Container
 * Main container component that manages view mode, date navigation, and rendering of Day/Week/Month views
 * 
 * @created 10-10-2025 20:58:00
 */
export const RoomCalendarView: React.FC<RoomCalendarViewProps> = ({
  rooms,
  sessionTemplates,
  cellStatuses,
  assignments = [],
  isLoading = false,
  initialViewMode = 'week',
  initialDate,
  onCellClick,
  onAssignmentClick,
  onDateRangeChange,
}) => {
  // State management
  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => {
    // Try to load from localStorage
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return (stored as CalendarViewMode) || initialViewMode;
  });
  
  // FIXED: 15-10-2025 18:30:00 - Use date-fns for proper date initialization
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (initialDate) {
      // Use initialDate if provided (from EnhancedPlannerPage)
      return new Date(initialDate);
    }
    // Fallback to today with proper date-fns handling
    return new Date();
  });

  // FIXED: 10-10-2025 22:38:16 - Prevent useEffect from running on initial mount
  const isInitialMount = useRef(true);

  // FIXED: 10-10-2025 21:14:24 - Use ref to store callback and prevent infinite loops
  const onDateRangeChangeRef = useRef(onDateRangeChange);
  onDateRangeChangeRef.current = onDateRangeChange;

  // FIXED: 10-10-2025 21:14:24 - Debounced date range change to prevent rapid API calls
  const debouncedDateRangeChange = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (startDate: string, endDate: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (onDateRangeChangeRef.current) {
          onDateRangeChangeRef.current(startDate, endDate);
        }
      }, 150); // 150ms debounce delay
    };
  }, []);

  // REMOVED: 15-10-2025 13:45:00 - Eliminated bi-directional sync to fix infinite loop
  // This useEffect was creating circular dependency:
  // parent updates initialDate → child updates currentDate → triggers onDateRangeChange → parent updates dateRange → LOOP
  // 
  // CORRECT PATTERN: Uncontrolled component with initial value
  // - Accept initialDate ONCE on mount (via useState initializer)
  // - Manage currentDate internally
  // - Notify parent via onDateRangeChange (one-way data flow)
  // - NEVER sync back from parent's updated date

  // FIXED: 10-10-2025 21:30:00 - Memoized callback to prevent recreation on every render
  const handleDateClick = useCallback((date: Date) => {
    // Batch state updates to prevent double useEffect triggers
    React.startTransition(() => {
      setCurrentDate(date);
      setViewMode('day');
    });
  }, []);

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // FIXED: 15-10-2025 13:45:00 - Clean one-way data flow to parent
  // Notify parent of date range changes when currentDate or viewMode changes
  useEffect(() => {
    // Skip initial mount to prevent unnecessary API call on first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      console.log('📅 [RoomCalendarView] Initial mount, skipping date range sync');
      return;
    }

    // Calculate date range for current view and notify parent
    const { startDate, endDate } = getDateRangeForView(currentDate, viewMode);
    console.log(`📅 [RoomCalendarView] Date range change triggered: ${viewMode} view`, {
      currentDate: currentDate.toISOString().split('T')[0],
      startDate,
      endDate,
      range: `${startDate} to ${endDate}`,
      trigger: 'user interaction (navigation/view change)'
    });
    
    // Notify parent with debounced call (one-way communication)
    debouncedDateRangeChange(startDate, endDate);
  }, [currentDate, viewMode, debouncedDateRangeChange]);

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentDate((prev) => navigatePrevious(prev, viewMode));
  };

  const handleNext = () => {
    setCurrentDate((prev) => navigateNext(prev, viewMode));
  };

  const handleToday = () => {
    setCurrentDate(getTodayMidnight());
  };

  const isToday = () => {
    const today = getTodayMidnight();
    const current = new Date(currentDate);
    current.setHours(0, 0, 0, 0);
    return current.getTime() === today.getTime();
  };

  // Calculate statistics
  const totalSlotsPerDay = rooms.length * sessionTemplates.length;

  return (
    <Stack gap="md">
      {/* Header with controls */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          {/* Top row: View mode selector and date navigation */}
          <Group justify="space-between" align="center" wrap="nowrap">
            {/* View mode selector */}
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as CalendarViewMode)}
              data={[
                {
                  value: 'day',
                  label: (
                    <Group gap="xs" wrap="nowrap">
                      <IconLayoutList size={16} />
                      <Text size="sm">Day</Text>
                    </Group>
                  ),
                },
                {
                  value: 'week',
                  label: (
                    <Group gap="xs" wrap="nowrap">
                      <IconLayoutGrid size={16} />
                      <Text size="sm">Week</Text>
                    </Group>
                  ),
                },
                {
                  value: 'month',
                  label: (
                    <Group gap="xs" wrap="nowrap">
                      <IconCalendar size={16} />
                      <Text size="sm">Month</Text>
                    </Group>
                  ),
                },
              ]}
            />

            {/* Date navigation */}
            <Group gap="sm" wrap="nowrap">
              <ActionIcon
                variant="light"
                size="lg"
                onClick={handlePrevious}
                aria-label="Previous period"
              >
                <IconChevronLeft size={18} />
              </ActionIcon>

              <div style={{ minWidth: 200, textAlign: 'center' }}>
                <Text fw={600} size="lg">
                  {formatCalendarHeader(currentDate, viewMode)}
                </Text>
              </div>

              <ActionIcon
                variant="light"
                size="lg"
                onClick={handleNext}
                aria-label="Next period"
              >
                <IconChevronRight size={18} />
              </ActionIcon>

              {!isToday() && (
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleToday}
                  leftSection={<IconCalendarEvent size={16} />}
                >
                  Today
                </Button>
              )}
            </Group>
          </Group>

          {/* Bottom row: Statistics and filters */}
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Badge size="lg" variant="light" color="blue">
                {rooms.length} Rooms
              </Badge>
              <Badge size="lg" variant="light" color="green">
                {sessionTemplates.length} Sessions
              </Badge>
              <Badge size="lg" variant="light" color="purple">
                {totalSlotsPerDay} Slots/Day
              </Badge>
            </Group>

            <Group gap="sm">
              <Text size="xs" c="dimmed">
                View: {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}
              </Text>
            </Group>
          </Group>
        </Stack>
      </Paper>

      {/* Calendar view content */}
      <div>
        {viewMode === 'day' && (
          <DayView
            currentDate={currentDate}
            rooms={rooms}
            sessionTemplates={sessionTemplates}
            cellStatuses={cellStatuses}
            assignments={assignments}
            isLoading={isLoading}
            groupByFloor={true}
            onCellClick={onCellClick}
            onAssignmentClick={onAssignmentClick}
          />
        )}

        {viewMode === 'week' && (
          <WeekView
            currentDate={currentDate}
            rooms={rooms}
            sessionTemplates={sessionTemplates}
            cellStatuses={cellStatuses}
            assignments={assignments}
            isLoading={isLoading}
            onCellClick={onCellClick}
            onAssignmentClick={onAssignmentClick}
          />
        )}

        {viewMode === 'month' && (
          <MonthView
            currentDate={currentDate}
            totalSlotsPerDay={totalSlotsPerDay}
            cellStatuses={cellStatuses}
            assignments={assignments}
            isLoading={isLoading}
            onDateClick={handleDateClick}
          />
        )}
      </div>
    </Stack>
  );
};

export default RoomCalendarView;

